import { PipeTransform } from '@angular/core';

import { BooleanValue, DataType, ExtendedDataType } from '@shared/models';
import {
  StatusTrackerColumn,
  TableOfColumnItemWithValues,
} from '@shared/models/project-setup';
import {
  getDecimalCount,
  getOptionId,
  getOptionName,
  isDecimalNumber,
  removeTrailingZeroDigits,
} from '.';
import { BooleanTerminology } from '@shared/enums/boolean-terminology';

const summarizableDataTypes = [
  DataType.Number,
  DataType.Percentage,
  DataType.Boolean,
  ExtendedDataType.Formula,
];

export function hasValue(
  value: any,
  dataTypeId: DataType,
  valueAsOption?: boolean
): boolean {
  switch (dataTypeId) {
    case DataType.FreeText:
      return !String.isNullOrWhiteSpace(value);
    case DataType.Number:
      return !isNaN(parseFloat(value));
    case DataType.List:
    case DataType.Boolean:
      const optionValue = valueAsOption ? getOptionId(value) : value;
      return optionValue != null && optionValue !== -1;
    default:
      return value;
  }
}

export function getValue(
  value: any,
  dataTypeId: DataType,
  options: any[],
  datePipe?: PipeTransform,
  decimalPrecision?: number
): string | number {
  if (!dataTypeId) {
    return value;
  }

  if (value == null || String.isNullOrWhiteSpace(String(value))) {
    return '-';
  }

  switch (dataTypeId) {
    case DataType.Number:
      if (isDecimalNumber(value)) {
        return removeTrailingZeroDigits(
          Number(value),
          decimalPrecision ?? getDecimalCount(Number(value))
        );
      } else {
        return value;
      }
    case DataType.Percentage:
      return `${parseFloat(value)}%`;
    case DataType.Date:
      return datePipe && isValidDate
        ? datePipe.transform(value, 'dd-MM-yy')
        : value;
    case DataType.Datetime:
      return datePipe && isValidDate
        ? datePipe.transform(value, 'dd-MM-yy HH:mm')
        : value;
    case DataType.List:
    case DataType.Boolean:
      return options ? getOptionName(+value, options) ?? '-' : value;
    case DataType.FreeText:
    default:
      return value;
  }
}

export function isConfigurationDataType(
  dataTypeId: number,
  isExtendedConfiguration: boolean = false
): boolean {
  return (
    dataTypeId === ExtendedDataType.List ||
    dataTypeId === ExtendedDataType.Boolean ||
    (isExtendedConfiguration && isDecimalDataType(dataTypeId))
  );
}

export function isDataTypeSummarizable(dataTypeId: number): boolean {
  return summarizableDataTypes.includes(dataTypeId);
}

/**
 * Reports whether a column holds the configuration its data type uses — a
 * formula for a formula column, an enabled carry-over for a numeric one. The
 * Configuration cell and its tooltip both read this, so they cannot disagree
 * about whether the column is configured.
 */
export function isColumnConfigured(
  column: TableOfColumnItemWithValues | StatusTrackerColumn
): boolean {
  return column?.dataTypeId === ExtendedDataType.Formula
    ? !!column?.formula?.expression
    : !!column?.carryOver?.carryOverEnabled;
}

export function buildBooleanValues(
  booleanTerminologyId: number,
  canBeDeleted?: boolean
): BooleanValue[] {
  return [
    {
      id: 0,
      value: BooleanTerminology[booleanTerminologyId].falseValue,
      canBeDeleted,
    },
    {
      id: 1,
      value: BooleanTerminology[booleanTerminologyId].trueValue,
      canBeDeleted,
    },
  ];
}

/** Data types whose cells hold a number with up to 2 decimals, typed or worked out. */
export function isDecimalDataType(dataTypeId: number): boolean {
  return (
    dataTypeId === ExtendedDataType.Number ||
    dataTypeId === ExtendedDataType.Percentage ||
    dataTypeId === ExtendedDataType.Formula
  );
}

function isValidDate(date: string): boolean {
  return (new Date(date) as any) !== 'Invalid Date' && !isNaN(+new Date(date));
}
